import { Button, Modal } from "flowbite-react";
import { useCallback, useEffect, useState } from "react";
import { Event } from "../../../Models/Event";
import EventParticipantsListTable from "./EventParticipantsListTable";
import { EventParticipant } from "../../../Models/EventParticipant";
import { RowSelectionState } from "@tanstack/react-table";
import { BsQrCode } from "react-icons/bs";
import EventApi from "../../../Api/EventApi";
import { toast } from "react-toastify";
import Loader from "../../Common/Loader";

export default function EventParticipantsModal({
  show,
  setShow,
  selectedEvent,
  onDeleteParticipant,
}: {
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  selectedEvent: Event | null;
  onDeleteParticipant: (participant: any) => void;
}) {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [selectedParticipants, setSelectedParticipants] = useState<
    EventParticipant[]
  >([]);
  const [participants, setParticipants] = useState<EventParticipant[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const loadParticipants = useCallback(async (eventId: number) => {
    try {
      setIsLoading(true);
      const res = await EventApi.getEventParticipants(eventId);
      setParticipants(res.data ?? []);
    } catch (error) {
      console.error(error);
      toast.error("Error loading participants");
      setParticipants([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!show || !selectedEvent?.id) {
      setParticipants([]);
      return;
    }
    setRowSelection({});
    loadParticipants(selectedEvent.id);
  }, [show, selectedEvent?.id, loadParticipants]);

  function onCloseModal() {
    setShow(false);
    setRowSelection({});
  }

  const clearSelection = () => {
    setRowSelection({});
  };

  const downloadBadges = async () => {
    if (!selectedEvent || selectedParticipants.length === 0) return;

    try {
      setIsLoading(true);
      const participantIds = selectedParticipants.map((p) => p.id);
      const response = await EventApi.getEventParticipantBadges(
        selectedEvent.id,
        participantIds
      );

      const pdfBlob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(pdfBlob);
      const tempLink = document.createElement("a");
      tempLink.href = url;
      tempLink.setAttribute("download", `${selectedEvent.name}_Badges.pdf`);

      document.body.appendChild(tempLink);
      tempLink.click();

      document.body.removeChild(tempLink);
      window.URL.revokeObjectURL(url);
      setIsLoading(false);
      toast.success("Badges downloaded successfully");
    } catch (error) {
      console.error("Error downloading badges:", error);
      toast.error("Error downloading badges");
      setIsLoading(false);
    }
  };
  return (
    <>
      <Modal show={show} size="4xl" onClose={onCloseModal} popup>
        <Modal.Header></Modal.Header>
        <Modal.Body>
          {isLoading && <Loader />}
          <div className="w-full">
            <div className="mx-1 flex items-end justify-between mb-4">
              <div className="flex flex-1 justify-center">
                <h1 className="text-gray-900 font-bold text-4xl">
                  Event: {selectedEvent ? selectedEvent.name : "N/A"}
                </h1>
              </div>
              <div className="flex gap-2">
                {selectedParticipants.length > 0 && (
                  <Button onClick={clearSelection} className="buttonMain">
                    Clear Selection
                  </Button>
                )}
                <Button
                  onClick={downloadBadges}
                  className="buttonMainBlue"
                  disabled={selectedParticipants.length === 0}
                >
                  <BsQrCode size={20} className="mr-2" />
                  <span>
                    Download{" "}
                    {selectedParticipants.length > 0
                      ? selectedParticipants.length
                      : ""}{" "}
                    Badges
                  </span>
                </Button>
              </div>
            </div>
            <EventParticipantsListTable
              data={participants}
              onDeleteParticipant={async (participant) => {
                await onDeleteParticipant(participant);
                if (selectedEvent?.id) loadParticipants(selectedEvent.id);
              }}
              rowSelection={rowSelection}
              setRowSelection={setRowSelection}
              setSelectedParticipants={setSelectedParticipants}
            />
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}
